import React from 'react';
import { Link } from 'react-router-dom';

export const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      {/* Navigation Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/official.png" alt="Verisure" className="h-9 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4 text-xs font-bold">
            <Link to="/technicians" className="text-slate-400 hover:text-white transition">Find Technicians</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white transition">Terms</Link>
            <Link to="/login" className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-xl">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16 max-w-5xl space-y-16">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            <span>Our Mission & Architecture</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight">
            The World’s Universal <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-400 to-amber-400">Maintenance & Evidence</span> Ledger
          </h1>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed">
            We bridge the critical trust gap between equipment owners, field technicians, and insurance companies by replacing paper receipts with an immutable, photographic breakdown ledger.
          </p>
        </div>

        {/* The Problem & Solution Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl space-y-4">
            <div className="w-12 h-12 bg-red-500/10 border border-rose-500/30 text-red-400 rounded-2xl flex items-center justify-center">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white">The Problem We Solved</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              When industrial machinery, telecom nodes, or consumer assets break down, equipment owners face painful insurance claim rejections due to lack of verifiable servicing records. At the same time, insurers lose billions to fraudulent breakdown claims, and used machinery buyers have zero visibility into real repair history.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl space-y-4">
            <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-2xl flex items-center justify-center">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-white">The Verisure Solution</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Verisure provides physical QR Identity tags linked to a tamper-proof cryptographic ledger. Every service, replacement part, diagnostic reading, and breakdown photo is committed immutably. Insurers verify claims in seconds, owners preserve up to +35% resale value, and certified technicians build verified reputations.
            </p>
          </div>
        </div>

        {/* 4 Core Pillars of Verisure */}
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-black text-white">How Verisure Powers the Ecosystem</h2>
            <p className="text-slate-400 text-sm mt-1">Four interconnected pillars ensuring accountability across the asset lifecycle</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-900/80 border border-slate-800 p-6 rounded-3xl flex flex-col items-start">
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <rect x="7" y="7" width="3" height="3"/>
                  <rect x="14" y="7" width="3" height="3"/>
                  <rect x="7" y="14" width="3" height="3"/>
                  <rect x="14" y="14" width="3" height="3"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base mb-2">Adaptive QR Tags</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Precision-sized for equipment scales (Compact 35mm to Industrial 220mm) with Level-H error correction and weather resistance.
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 p-6 rounded-3xl flex flex-col items-start">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base mb-2">Insurance Evidence</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Tamper-proof before/after photos, OEM part verification, and GPS timestamps formatted for instant insurance claim auditing.
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 p-6 rounded-3xl flex flex-col items-start">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base mb-2">Resale Maximizer</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                Dynamic valuation engine calculating verifiable maintenance bonuses and depreciation curves for secondary market buyers.
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 p-6 rounded-3xl flex flex-col items-start">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base mb-2">Vetted Technicians</h4>
              <p className="text-slate-400 text-xs leading-relaxed">
                KYC-verified network of qualified field engineers with transparent owner ratings and real-time maintenance portfolios.
              </p>
            </div>
          </div>
        </div>

        {/* Visual Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <h2 className="text-2xl md:text-4xl font-black text-white mb-4">
            Protect Your Assets. Validate Your Repairs.
          </h2>
          <p className="text-slate-300 text-sm max-w-xl mx-auto mb-6">
            Join thousands of enterprise facility managers, equipment owners, and certified technicians tracking lifecycle history on Verisure.
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to="/items"
              className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-bold px-6 py-3 rounded-2xl shadow-lg shadow-orange-500/25 hover:opacity-95 transition text-sm"
            >
              Register Equipment Now
            </Link>
            <Link
              to="/technicians"
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-bold px-6 py-3 rounded-2xl transition text-sm"
            >
              Find a Technician
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 border-t border-slate-800 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} VERISURE • Secure. Assured. Always.</p>
      </footer>
    </div>
  );
};
